import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["menu", "button"];

  toggle() {
    if (this.menuTarget.classList.contains("hidden")) {
      this.showMenu();
    } else {
      this.hideMenu();
    }
  }

  showMenu() {
    // Add transition classes first
    this.menuTarget.style.transition = "all 0.3s ease-in-out";
    this.menuTarget.classList.remove("hidden");
    this.menuTarget.classList.add("flex");

    // Use requestAnimationFrame to ensure the transition happens
    requestAnimationFrame(() => {
      this.menuTarget.style.opacity = "1";
      this.menuTarget.style.transform = "translateY(0)";
    });

    // Add aria-expanded for accessibility
    this.buttonTarget.setAttribute("aria-expanded", "true");

    // Lock background scroll while the full-height menu is open
    document.body.style.overflow = "hidden";

    // Change hamburger to X icon with transition
    const hamburgerIcon = this.buttonTarget.querySelector(".hamburger-icon");
    const closeIcon = this.buttonTarget.querySelector(".close-icon");
    if (hamburgerIcon && closeIcon) {
      hamburgerIcon.style.transition = "opacity 0.2s ease-in-out";
      closeIcon.style.transition = "opacity 0.2s ease-in-out";
      hamburgerIcon.style.opacity = "0";
      closeIcon.style.opacity = "1";

      setTimeout(() => {
        hamburgerIcon.classList.add("hidden");
        closeIcon.classList.remove("hidden");
      }, 200);
    }
  }

  hideMenu() {
    // Animate out first
    this.menuTarget.style.opacity = "0";
    this.menuTarget.style.transform = "translateY(-10px)";

    // Hide after animation
    setTimeout(() => {
      this.menuTarget.classList.add("hidden");
      this.menuTarget.classList.remove("flex");
    }, 300);

    // Add aria-expanded for accessibility
    this.buttonTarget.setAttribute("aria-expanded", "false");

    // Restore background scroll
    document.body.style.overflow = "";

    // Change X back to hamburger icon with transition
    const hamburgerIcon = this.buttonTarget.querySelector(".hamburger-icon");
    const closeIcon = this.buttonTarget.querySelector(".close-icon");
    if (hamburgerIcon && closeIcon) {
      hamburgerIcon.style.transition = "opacity 0.2s ease-in-out";
      closeIcon.style.transition = "opacity 0.2s ease-in-out";
      closeIcon.style.opacity = "0";
      hamburgerIcon.style.opacity = "1";

      setTimeout(() => {
        hamburgerIcon.classList.remove("hidden");
        closeIcon.classList.add("hidden");
      }, 200);
    }
  }

  // Hide menu when clicking on a navigation link
  hideOnLinkClick(event) {
    // Only hide if we're on mobile (menu is visible)
    if (!this.menuTarget.classList.contains("hidden")) {
      this.hideMenu();
    }
  }

  // Hide menu when clicking outside
  hideOnOutsideClick(event) {
    if (
      !this.element.contains(event.target) &&
      !this.menuTarget.classList.contains("hidden")
    ) {
      this.hideMenu();
    }
  }

  // Handle escape key
  handleKeydown(event) {
    if (
      event.key === "Escape" &&
      !this.menuTarget.classList.contains("hidden")
    ) {
      this.hideMenu();
    }
  }

  // Set up event listeners
  initialize() {
    this.hideOnOutsideClickBound = this.hideOnOutsideClick.bind(this);
    this.handleKeydownBound = this.handleKeydown.bind(this);
  }

  connect() {
    document.addEventListener("click", this.hideOnOutsideClickBound);
    document.addEventListener("keydown", this.handleKeydownBound);

    // Initialize menu with proper styles for animation
    this.menuTarget.style.opacity = "0";
    this.menuTarget.style.transform = "translateY(-10px)";

    this.hideMenu();
  }

  disconnect() {
    document.removeEventListener("click", this.hideOnOutsideClickBound);
    document.removeEventListener("keydown", this.handleKeydownBound);
  }
}
